import React, { useState, useEffect } from 'react';
import { Search, Filter, X, ArrowLeft } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import { PostGrid } from '../components/PostGrid';
import { usePostsWithPagination } from '../hooks/usePosts';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const PostsPage: React.FC = () => {
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams] = useSearchParams();
  
  const {
    posts,
    loading,
    error,
    total,
    totalPages,
    currentPage,
    filters,
    goToPage,
    setPerPage,
    setCategory,
    setSearch,
    setOrderBy,
    setOrder,
    clearFilters,
  } = usePostsWithPagination({
    perPage: 9,
    orderBy: 'date',
    order: 'desc',
  });

  // Handle URL parameters for category filtering
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl && categoryFromUrl !== filters.category) {
      setCategory(categoryFromUrl);
    }
  }, [searchParams, setCategory, filters.category]);

  // Debug posts changes
  useEffect(() => {
    if (filters.category) {
    }
  }, [posts, filters.category]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchQuery);
  };

  const handleClearFilters = () => {
    clearFilters();
    setSearchQuery('');
  };

  const hasActiveFilters = filters.category || filters.tag || filters.search || filters.orderBy !== 'date' || filters.order !== 'desc';

  return (
    <div className="min-h-screen bg-primary text-tertiary">
      <Header />
      {/* Page Header */}
      <div className="bg-primarySupport shadow-sm">
        <div className="container mx-auto px-4 py-8">
          {filters.category && (
            <div className="mb-4">
              <Link
                to="/blog-hub"
                className="inline-flex items-center gap-2 text-tertiary hover:text-primarySupport transition-colors font-semibold"
              >
                <ArrowLeft size={20} />
                Back to Blog Hub
              </Link>
            </div>
          )}
          <h1 className="text-4xl font-bold text-center mb-4">
            {filters.category ? `${filters.category.toUpperCase()} POSTS` : 'PROTEIN RECIPES & TIPS'}
          </h1>
          <p className="text-tertiary/90 text-center max-w-2xl mx-auto">
            {filters.category 
              ? `Discover all posts in the ${filters.category} category.`
              : 'Discover delicious protein recipes, nutrition tips, and fitness advice to help you achieve your health goals.'
            }
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-primarySupport shadow-md p-6 mb-8">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tertiary/60" size={20} />
              <input
                type="text"
                placeholder="Search recipes and tips..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-tertiary/40 bg-primary text-tertiary focus:ring-2 focus:ring-tertiary focus:border-transparent"
              />
              <button
                type="submit"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-tertiary text-white px-4 py-1 hover:bg-primarySupport transition-colors"
              >
                Search
              </button>
            </div>
          </form>

          {/* Filter Toggle */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-tertiary/40 hover:bg-black/20 transition-colors"
            >
              <Filter size={16} />
              Filters
            </button>

            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-2 px-4 py-2 text-tertiary hover:text-tertiary transition-colors"
              >
                <X size={16} />
                Clear Filters
              </button>
            )}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-6 p-4 bg-black/20">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-medium text-tertiary/90 mb-2">Sort By</label>
                  <select
                    value={filters.orderBy || 'date'}
                    onChange={(e) => setOrderBy(e.target.value as 'date' | 'title' | 'modified')}
                    className="w-full px-3 py-2 border border-tertiary/40 bg-primary text-tertiary focus:ring-2 focus:ring-tertiary focus:border-transparent"
                  >
                    <option value="date">Date</option>
                    <option value="title">Title</option>
                    <option value="modified">Last Modified</option>
                  </select>
                </div>

                {/* Sort Direction */}
                <div>
                  <label className="block text-sm font-medium text-tertiary/90 mb-2">Order</label>
                  <select
                    value={filters.order || 'desc'}
                    onChange={(e) => setOrder(e.target.value as 'asc' | 'desc')}
                    className="w-full px-3 py-2 border border-tertiary/40 bg-primary text-tertiary focus:ring-2 focus:ring-tertiary focus:border-transparent"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>

                {/* Posts Per Page */}
                <div>
                  <label className="block text-sm font-medium text-tertiary/90 mb-2">Posts Per Page</label>
                  <select
                    value={filters.perPage || 9}
                    onChange={(e) => setPerPage(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-tertiary/40 bg-primary text-tertiary focus:ring-2 focus:ring-tertiary focus:border-transparent"
                  >
                    <option value={6}>6</option>
                    <option value={9}>9</option>
                    <option value={12}>12</option>
                    <option value={18}>18</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-tertiary/90">
            Showing {posts.length} of {total} posts
          </p>
          {hasActiveFilters && (
            <div className="flex items-center gap-2 text-sm text-tertiary/80">
              <span>Active filters:</span>
              {filters.search && (
                <span className="px-2 py-1 bg-tertiary/20 text-tertiary">Search: {filters.search}</span>
              )}
              {filters.category && (
                <span className="px-2 py-1 bg-tertiary/20 text-tertiary">Category: {filters.category}</span>
              )}
              {filters.tag && (
                <span className="px-2 py-1 bg-tertiary/20 text-tertiary">Tag: {filters.tag}</span>
              )}
            </div>
          )}
        </div>

        {/* Posts Grid */}
        <PostGrid
          posts={posts}
          loading={loading}
          error={error}
          columns={3}
          showExcerpt={true}
          showReadingTime={true}
          imageSize="medium"
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-12">
            <nav className="flex items-center gap-2">
              {/* Previous Page */}
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-tertiary/40 hover:bg-black/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {/* Page Numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`px-3 py-2 border rounded-md ${
                    currentPage === page
                      ? 'bg-tertiary text-white border-tertiary'
                      : 'border-tertiary/40 hover:bg-black/20'
                  }`}
                >
                  {page}
                </button>
              ))}

              {/* Next Page */}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-tertiary/40 hover:bg-black/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PostsPage;
